/**
 * Inline variant picker for product cards.
 * Maps the selected option pills to a variant, then updates the add-to-cart
 * form's variant id, the card price and the button availability.
 */
if (!customElements.get('card-variant-picker')) {
    customElements.define('card-variant-picker', class CardVariantPicker extends HTMLElement {
        connectedCallback() {
            const dataEl = this.querySelector('.product-card-variants-data');
            if (!dataEl) return;
            try {
                this.variants = JSON.parse(dataEl.textContent);
            } catch (e) {
                return;
            }
            const pricesEl = this.querySelector('.product-card-variants-prices');
            try {
                this.prices = pricesEl ? JSON.parse(pricesEl.textContent) : {};
            } catch (e) {
                this.prices = {};
            }

            this.card = this.closest('.product-card');
            // The add-to-cart form lives in the buttons area of the same card
            this.productForm = this.card ? this.card.querySelector('product-form') : null;

            this.makeUniqueNames();

            this.inputs = Array.from(this.querySelectorAll('.product-card-variant-input'));
            this.inputs.forEach((input) => {
                input.addEventListener('change', () => this.update());
            });

            this.ensureSelection();
            this.update();
            this.setupResponsiveSelects();
        }

        disconnectedCallback() {
            if (this._ro) this._ro.disconnect();
        }

        // Make radio group names/ids unique per element instance. The server's
        // increment-based id resets on every render, so a product that appears both
        // server-rendered and in an async-loaded block (tabs, recommendations) ends
        // up with the SAME radio name on two cards — the browser merges them into one
        // radio group and only one card keeps its checked state. Re-scoping names at
        // runtime guarantees each card is independent regardless of how it loaded.
        makeUniqueNames() {
            const uid = 'cv' + (window.__cardVariantUid = (window.__cardVariantUid || 0) + 1) + '-';
            this.querySelectorAll('.product-card-variant-input').forEach((input) => {
                const oldId = input.id;
                const label = oldId ? this.querySelector('label[for="' + CSS.escape(oldId) + '"]') : null;
                input.name = uid + input.name;
                if (oldId) input.id = uid + oldId;
                if (label) label.setAttribute('for', input.id);
            });
            this.querySelectorAll('[data-variant-select]').forEach((select) => {
                if (select.dataset.optionName) select.dataset.optionName = uid + select.dataset.optionName;
            });
        }

        // Guarantee a valid initial selection. If nothing resolves to a variant
        // (e.g. the checked state was lost to a name collision before this ran),
        // default to the first available variant — mirrors Liquid's
        // selected_or_first_available_variant.
        ensureSelection() {
            if (this.findVariant(this.getSelectedOptions())) return;
            const target = this.variants.find((v) => v.available) || this.variants[0];
            if (!target) return;
            Array.from(this.querySelectorAll('[data-variant-row]')).forEach((row, i) => {
                const val = target.options[i];
                if (val == null) return;
                const radio = row.querySelector('.product-card-variant-input[value="' + CSS.escape(val) + '"]');
                if (radio) radio.checked = true;
                const select = row.querySelector('[data-variant-select]');
                if (select) select.value = val;
            });
        }

        // When an option's pills wrap past a single row, collapse that row into a
        // <select> so tall cards stay compact. The radios remain the source of
        // truth — the select just drives the matching radio, so update() is unchanged.
        setupResponsiveSelects() {
            this.rows = Array.from(this.querySelectorAll('[data-variant-row]'));
            const rowsWithSelect = this.rows.filter((row) => row.querySelector('[data-variant-select]'));
            if (rowsWithSelect.length === 0) return;

            rowsWithSelect.forEach((row) => {
                const select = row.querySelector('[data-variant-select]');
                select.addEventListener('change', () => {
                    const name = select.dataset.optionName;
                    const radio = this.querySelector(
                        '.product-card-variant-input[name="' + CSS.escape(name) + '"][value="' + CSS.escape(select.value) + '"]'
                    );
                    if (radio) {
                        radio.checked = true;
                        radio.dispatchEvent(new Event('change', { bubbles: true }));
                    }
                });
            });

            const evaluate = () => rowsWithSelect.forEach((row) => this.collapseIfWraps(row));
            requestAnimationFrame(evaluate);
            if ('ResizeObserver' in window) {
                this._ro = new ResizeObserver(() => evaluate());
                this._ro.observe(this);
            }
        }

        collapseIfWraps(row) {
            const list = row.querySelector('.product-card-variants-list');
            const select = row.querySelector('[data-variant-select]');
            if (!list || !select) return;
            const pills = list.querySelectorAll('.product-card-variant-pill');
            if (pills.length === 0) return;

            // Force pills visible to measure, then restore — all synchronous, so the
            // browser never paints the intermediate state (no flicker).
            row.classList.add('_measuring');
            const top = pills[0].offsetTop;
            let wraps = false;
            for (let i = 1; i < pills.length; i++) {
                if (pills[i].offsetTop > top + 1) { wraps = true; break; }
            }
            row.classList.remove('_measuring');

            if (wraps) {
                const checked = list.querySelector('.product-card-variant-input:checked');
                if (checked) select.value = checked.value;
            }
            row.classList.toggle('_collapsed', wraps);
        }

        getSelectedOptions() {
            const byName = {};
            const order = [];
            this.inputs.forEach((input) => {
                if (!(input.name in byName)) {
                    byName[input.name] = null;
                    order.push(input.name);
                }
                if (input.checked) byName[input.name] = input.value;
            });
            return order.map((name) => byName[name]);
        }

        findVariant(selected) {
            return this.variants.find((variant) =>
                variant.options.every((option, index) => option === selected[index])
            );
        }

        update() {
            const selected = this.getSelectedOptions();
            const variant = this.findVariant(selected);

            this.markAvailability();

            const idInput = this.productForm && this.productForm.querySelector('input[name="id"]');
            const addBtn = this.productForm && this.productForm.querySelector('button[name="add"]');

            if (idInput) idInput.value = variant ? variant.id : '';

            if (addBtn) {
                const ok = !!(variant && variant.available);
                addBtn.disabled = !ok;
                const txt = addBtn.querySelector('.text');
                if (txt && addBtn.dataset.addText && addBtn.dataset.soldoutText) {
                    txt.textContent = ok ? addBtn.dataset.addText : addBtn.dataset.soldoutText;
                }
            }

            if (variant && this.card) {
                const priceInfo = this.prices[variant.id];
                const currentEl = this.card.querySelector('.product-card-price-current');
                const oldEl = this.card.querySelector('.product-card-price-old del');
                if (currentEl && priceInfo) currentEl.textContent = priceInfo.price;
                if (oldEl && priceInfo) {
                    oldEl.textContent = priceInfo.compare || '';
                    oldEl.parentElement.style.display = priceInfo.compare ? '' : 'none';
                }

                this.updateImage(variant);
            }
        }

        // Mark option values that have no available variant (given the other
        // selected options) as sold-out: struck-through pills + "– Utsolgt" in
        // the collapsed dropdown. Radios stay enabled so they're still selectable.
        markAvailability() {
            if (!window.VariantAvailability || !this.variants) return;
            const rows = Array.from(this.querySelectorAll('[data-variant-row]'));
            if (rows.length === 0) return;
            const groups = rows.map((row) => {
                const inputs = Array.from(row.querySelectorAll('.product-card-variant-input'));
                const checked = inputs.find((i) => i.checked);
                const select = row.querySelector('[data-variant-select]');
                const soldoutText = select ? (select.dataset.soldoutText || '') : '';
                return {
                    selectedValue: checked ? checked.value : null,
                    items: inputs.map((input) => {
                        const label = row.querySelector('label[for="' + CSS.escape(input.id) + '"]');
                        const opt = select ? Array.from(select.options).find((o) => o.value === input.value) : null;
                        return {
                            value: input.value,
                            mark: (soldOut) => {
                                if (label) label.classList.toggle('_sold-out', soldOut);
                                if (opt) {
                                    if (!opt.dataset.baseLabel) opt.dataset.baseLabel = opt.textContent;
                                    opt.textContent = soldOut && soldoutText
                                        ? opt.dataset.baseLabel + ' – ' + soldoutText
                                        : opt.dataset.baseLabel;
                                }
                            }
                        };
                    })
                };
            });
            window.VariantAvailability.apply(this.variants, groups, '_sold-out');
        }

        updateImage(variant) {
            const image = variant && variant.featured_image;
            if (!image || !image.src) return;
            const picture = this.card.querySelector('.product-card-picture._front');
            if (!picture) return;
            const img = picture.querySelector('img');
            const sources = picture.querySelectorAll('source');
            const sep = image.src.indexOf('?') > -1 ? '&' : '?';
            const sized = (w) => image.src + sep + 'width=' + w;
            if (img) {
                img.srcset = sized(360) + ' 360w, ' + sized(533) + ' 533w, ' + sized(720) + ' 720w';
                img.src = sized(533);
            }
            sources.forEach((source) => {
                source.srcset = sized(360) + ' 360w, ' + sized(533) + ' 533w, ' + sized(720) + ' 720w';
            });
        }
    });
}
